import React, { useState, useRef } from "react";
import { HERO_TESTIMONIALS, MEDIA_TESTIMONIALS } from "../data/content";
import { Tag } from "./ui-primitives";

const TAB_DATA = {
  brands: { label: "Brands", items: HERO_TESTIMONIALS },
  media: { label: "Media", items: MEDIA_TESTIMONIALS },
};

const SWIPE_THRESHOLD = 50; // px — minimum horizontal travel to register a swipe

export default function HeroTestimonialSlider({ theme = "light" }) {
  const [tab, setTab] = useState("brands");
  const [idxByTab, setIdxByTab] = useState({ brands: 0, media: 0 });
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  const items = TAB_DATA[tab].items;
  const total = items.length;
  const idx = idxByTab[tab];
  const empty = total === 0;

  const isLight = theme === "light";
  const fg = isLight ? "var(--color-on-surface)" : "var(--color-on-primary)";
  const fgMuted = isLight ? "var(--color-on-surface-variant)" : "rgba(249,249,248,0.7)";
  const fgFaint = isLight ? "rgba(4,13,35,0.55)" : "rgba(249,249,248,0.55)";
  const cardBg = isLight ? "var(--color-surface-container-lowest)" : "rgba(249,249,248,0.04)";
  const cardBorder = isLight ? "1px solid var(--color-outline)" : "1px solid rgba(249,249,248,0.1)";
  const dotInactive = isLight ? "rgba(4,13,35,0.18)" : "rgba(249,249,248,0.2)";
  const initialBg = isLight ? "var(--color-surface-container-high)" : "var(--color-primary-container)";
  const initialColor = isLight ? "var(--color-primary)" : "rgba(249,249,248,0.85)";
  const logoBorder = isLight ? "1px solid var(--color-outline)" : "1px solid rgba(249,249,248,0.22)";
  const logoColor = isLight ? "var(--color-primary)" : "rgba(249,249,248,0.78)";
  const pillBorder = isLight ? "1px solid var(--color-outline)" : "1px solid rgba(249,249,248,0.18)";

  const setIdx = (next) => setIdxByTab((s) => ({ ...s, [tab]: next }));
  const go = (delta) => {
    if (total === 0) return;
    setIdx((idx + delta + total) % total);
  };

  return (
    <div data-testid="hero-testimonials">
      {/* Header — shared <Tag> component matches all other section headings */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5 mb-8">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
          <Tag>Trusted by:</Tag>
          <div className="flex items-center gap-2">
            {Object.entries(TAB_DATA).map(([key, { label, items: tabItems }]) => {
              const isActive = tab === key;
              const isEmpty = tabItems.length === 0;
              return (
                <button
                  key={key}
                  type="button"
                  data-testid={`testimonials-tab-${key}`}
                  onClick={() => setTab(key)}
                  aria-pressed={isActive}
                  className="label-md transition-colors"
                  style={{
                    padding: "8px 14px",
                    borderRadius: "var(--radius-pill)",
                    cursor: "pointer",
                    background: isActive ? "var(--color-tertiary)" : "transparent",
                    color: isActive ? "var(--color-on-tertiary)" : fg,
                    border: isActive ? "1px solid var(--color-tertiary)" : pillBorder,
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Empty state for Media tab */}
      {empty ? (
        <article
          data-testid="testimonials-empty"
          style={{
            background: cardBg,
            border: cardBorder,
            padding: "var(--space-48)",
            borderRadius: "var(--radius-sm)",
            textAlign: "center",
          }}
        >
          <div
            aria-hidden
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: "var(--color-tertiary)",
              margin: "0 auto 24px",
            }}
          />
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 500,
              fontSize: "clamp(1rem, 1.15vw, 1.1875rem)",
              lineHeight: 1.55,
              letterSpacing: "-0.005em",
              color: fg,
              maxWidth: 640,
              margin: "0 auto 12px",
            }}
          >
            We're currently inviting our media partners to share their experience working with DesignIndya.
          </p>
          <p className="label-sm" style={{ color: fgFaint }}>
            Watch this space.
          </p>
        </article>
      ) : (
        <>
          <div
            data-testid="testimonial-cards-wrap"
            style={{ overflow: "hidden", paddingBottom: 4, touchAction: "pan-y" }}
            onTouchStart={(e) => {
              const t = e.touches[0];
              touchStartX.current = t.clientX;
              touchStartY.current = t.clientY;
            }}
            onTouchEnd={(e) => {
              if (touchStartX.current === null) return;
              const t = e.changedTouches[0];
              const dx = t.clientX - touchStartX.current;
              const dy = t.clientY - (touchStartY.current ?? 0);
              touchStartX.current = null;
              touchStartY.current = null;
              // Only treat as horizontal swipe if X movement dominates Y.
              if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
                go(dx < 0 ? 1 : -1);
              }
            }}
          >
            <div
              className="flex"
              style={{
                gap: 24,
                transform: `translateX(calc(${-idx * 92}% - ${idx * 24}px))`,
                transition: "transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)",
              }}
            >
              {items.map((t, i) => (
                <article
                  key={t.id}
                  data-testid={`testimonial-card-${t.id}`}
                  aria-hidden={i !== idx}
                  onClick={() => i !== idx && setIdx(i)}
                  style={{
                    flex: "0 0 92%",
                    background: cardBg,
                    border: cardBorder,
                    padding: "var(--space-32)",
                    borderRadius: "var(--radius-sm)",
                    opacity: i === idx ? 1 : 0.4,
                    transition: "opacity 0.55s ease",
                    cursor: i === idx ? "default" : "pointer",
                  }}
                >
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-start">
                    <blockquote
                      className="md:col-span-9"
                      style={{
                        fontFamily: "var(--font-body)",
                        fontWeight: 500,
                        fontSize: "clamp(1rem, 1.15vw, 1.1875rem)",
                        lineHeight: 1.55,
                        letterSpacing: "-0.005em",
                        color: fg,
                      }}
                    >
                      <span
                        style={{
                          color: "var(--color-tertiary)",
                          fontFamily: "var(--font-display)",
                          fontWeight: 800,
                          fontSize: "1.5em",
                          lineHeight: 0,
                          verticalAlign: "-0.2em",
                          marginRight: 4,
                        }}
                      >
                        “
                      </span>
                      {t.quote}
                    </blockquote>

                    <div className="md:col-span-3 flex items-start gap-4">
                      <div
                        aria-hidden
                        title={`${t.name} — photo placeholder`}
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: "50%",
                          background: initialBg,
                          border: cardBorder,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          fontFamily: "var(--font-display)",
                          fontSize: "1rem",
                          fontWeight: 700,
                          color: initialColor,
                        }}
                      >
                        {t.initials}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            color: fg,
                            fontFamily: "var(--font-body)",
                            fontWeight: 700,
                            fontSize: "0.8125rem",
                          }}
                        >
                          {t.name}
                        </div>
                        <div style={{ color: fgFaint, fontSize: "0.75rem", marginTop: 2 }}>{t.role}</div>
                        <div
                          aria-hidden
                          title={`${t.company} — logo placeholder`}
                          style={{
                            marginTop: 10,
                            display: "inline-block",
                            padding: "4px 8px",
                            fontFamily: "var(--font-display)",
                            fontSize: "0.7rem",
                            fontWeight: 800,
                            letterSpacing: "0.16em",
                            color: logoColor,
                            border: logoBorder,
                            borderRadius: "var(--radius-sm)",
                          }}
                        >
                          {t.companyMark}
                        </div>
                        <div style={{ color: fgFaint, fontSize: "0.7rem", marginTop: 6 }}>{t.company}</div>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 mt-6">
            {items.map((t, i) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setIdx(i)}
                data-testid={`testimonial-dot-${i}`}
                aria-label={`Show testimonial ${i + 1}`}
                style={{
                  width: i === idx ? 24 : 6,
                  height: 6,
                  borderRadius: 3,
                  background: i === idx ? "var(--color-tertiary)" : dotInactive,
                  border: "none",
                  cursor: "pointer",
                  transition: "width 0.3s ease, background-color 0.3s ease",
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
